import { Info } from '@v2/components/campaign/Info';
import { StyledDataGrid } from './styledComponents/StyledDataGrid';
import {
  Stack,
  Typography,
  Divider,
  Grid,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  IconButton,
  Alert,
} from '@mui/material';
import { ModalSheetDialog as Dialog } from '../styledComponents';
import { CancelButtonStyled } from '@v2/components/campaign/CampaignAdGroupSection/styles';
import { Button } from '@v2/components/ui/Button';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ReviewEditsDialogProps } from '../../constants/types';
import { useBulkEdit } from '../../context';
import { reviewEditsColumns } from './constants';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ReviewInvalidError
} from '@components/modals/BulkEditModal/components/ReviewEditsPage/components/ReviewInvalidError';

export const ReviewEdits = ({
  rows,
  invalidCount,
  handleSelectionChange,
  onCancel,
  onBack,
  onNext,
  isNextEnabled,
  isUpdating = false,
  showWarning = false,
  open,
  rowSelectionModel,
}: ReviewEditsDialogProps) => {
  const { field, getHeaderText } = useBulkEdit();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="lg"
      sx={{ pt: 2, pb: 2 }}
      data-testid="review-edits-dialog"
    >
      <form id="review-edits-form">
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pt={1}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={onBack} size="large" sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h3">{getHeaderText()}</Typography>
            </Box>

            <IconButton onClick={onCancel} size="large" aria-label="close">
              <CloseIcon data-testid="CloseIcon" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2} sx={{ px: 3, flex: 1 }}>
            {invalidCount > 0 && (
              <Alert
                severity="error"
                icon={
                  <Box
                    sx={{
                      color: 'red.1',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      fontSize="20px"
                    />
                  </Box>
                }
                sx={{ paddingLeft: 4, gap: 1 }}
              >
                <ReviewInvalidError invalidCount={invalidCount} field={field}/>
                <Typography variant="body1">
                  {`Unselect the ${invalidCount > 1 ? `${invalidCount} Ad Groups` : 'Ad Group'} to proceed, click the back arrow to edit, or Cancel to exit.`}
                </Typography>
              </Alert>
            )}
            <StyledDataGrid
              columns={reviewEditsColumns}
              rows={rows}
              checkboxSelection
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnResize
              disableColumnSorting
              disableColumnPinning
              disableColumnReorder
              hideFooter
              getRowHeight={() => 'auto'}
              getEstimatedRowHeight={() => 160}
              onRowSelectionModelChange={handleSelectionChange}
              rowSelectionModel={rowSelectionModel}
              getRowClassName={params =>
                params.row.result?.body.errors ? 'row-theme--Error' : ''
              }
              sx={{ border: 0, px: 1 }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    errorState: false,
                  },
                },
                sorting: {
                  sortModel: [{ field: 'errorState', sort: 'asc' }],
                },
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions disableSpacing sx={{ px: 4, py: 3 }}>
          <Grid container direction="column" sx={{ width: '100%' }}>
            <Divider sx={{ width: '100%', mb: 3 }} />
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ minHeight: 'auto' }}
            >
              <Grid>
                <CancelButtonStyled variant="text" onClick={onCancel}>
                  Cancel
                </CancelButtonStyled>
              </Grid>
              <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ width: 'auto' }}
              >
                {showWarning && (
                  <Grid>
                    <Info type="error" variant="text" width={450}>
                      Your previous Ad Group schedule will be replaced if you
                      save these edits. Click Cancel to undo.
                    </Info>
                  </Grid>
                )}
                <Grid>
                  <Button
                    variant="contained"
                    type="button"
                    label="Save Edits"
                    disabled={!isNextEnabled}
                    onClick={onNext}
                    sx={{ visibility: isUpdating ? 'hidden' : 'visible' }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};
